import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BriefcaseBusiness,
  Clock3,
  Cpu,
  CreditCard,
  LogOut,
  ShieldCheck,
  UserCircle2,
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useRole } from '../../context/RoleContext';
import { useAdminData } from '../../context/AdminDataContext';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../lib/api/client';
import { AdminNotice } from './AdminState';
import { trackProductInteraction, trackProductPage } from '../../lib/productAnalytics';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type Role = 'super_admin' | 'normal_user';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  roles: Role[];
}

const navItems: NavItem[] = [
  { label: 'Overview', path: '/admin', icon: BarChart3, roles: ['super_admin', 'normal_user'] },
  { label: 'Workforce', path: '/admin/workforce', icon: ShieldCheck, roles: ['super_admin', 'normal_user'] },
  { label: 'Tracking', path: '/admin/tracking', icon: Clock3, roles: ['super_admin', 'normal_user'] },
  { label: 'Payroll', path: '/admin/payroll', icon: CreditCard, roles: ['super_admin', 'normal_user'] },
  { label: 'Devices', path: '/admin/devices', icon: Cpu, roles: ['super_admin'] },
  { label: 'Companies', path: '/admin/companies', icon: BriefcaseBusiness, roles: ['super_admin'] },
];

const AdminLayout: React.FC = () => {
  const { role, activeCompanyId, setActiveCompanyId, toggleRole } = useRole();
  const { clients, error, refreshAll } = useAdminData();
  const { user, signOutAdmin } = useAuth();
  const location = useLocation();
  const lastScreen = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (lastScreen.current === location.pathname) return;
    lastScreen.current = location.pathname;
    trackProductPage(location.pathname, role);
  }, [location.pathname, role]);

  React.useEffect(() => {
    if (!activeCompanyId && clients[0]?.id) {
      setActiveCompanyId(clients[0].id);
      return;
    }

    const exists = clients.some((client) => client.id === activeCompanyId);
    if (activeCompanyId && !exists && clients[0]?.id) {
      setActiveCompanyId(clients[0].id);
    }
  }, [activeCompanyId, clients, setActiveCompanyId]);

  const filteredNavItems = navItems.filter((item) => item.roles.includes(role));
  const activeCompany = clients.find((client) => client.id === activeCompanyId) ?? clients[0] ?? null;

  const pageTitle =
    navItems.find((item) =>
      item.path === '/admin'
        ? location.pathname === '/admin' || location.pathname === '/admin/'
        : location.pathname.startsWith(item.path)
    )?.label ?? 'Overview';

  return (
    <div className="admin-shell min-h-screen md:flex md:h-screen md:overflow-hidden">
      <aside className="hidden md:flex md:h-screen md:w-[16.5rem] md:shrink-0 md:flex-col md:border-r md:border-[#2d2721] md:bg-[#11100f] md:text-stone-100 md:sticky md:top-0">
        <div className="border-b border-[#26211d] px-5 py-6">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#b8a58e]">Waypoint Industries</p>
          <h1 className="mt-2 text-2xl font-semibold text-[#f6efe4]">Operations</h1>
        </div>

        <div className="px-5 pt-5">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8b7d6a]">Workspace</p>
          {role === 'super_admin' ? (
            <select
              value={activeCompany?.id ?? ''}
              onChange={(event) => { trackProductInteraction('company_switch'); setActiveCompanyId(event.target.value); }}
              className="w-full rounded-2xl border border-[#312a23] bg-[#181614] px-4 py-3 text-sm text-[#f4ece0] outline-none transition focus:border-[#27b4c3]"
              disabled={clients.length === 0}
            >
              {clients.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="rounded-2xl border border-[#312a23] bg-[#181614] px-4 py-3 text-sm font-medium text-[#f4ece0]">
              {activeCompany?.name ?? 'No company selected'}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.path === '/admin'
                ? location.pathname === '/admin' || location.pathname === '/admin/'
                : location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-all',
                  isActive
                    ? 'border-[#2c97a3] bg-[#132326] text-[#e8f8fb] shadow-[0_10px_30px_rgba(15,157,176,0.12)]'
                    : 'border-transparent text-[#b7ac9d] hover:border-[#2d2721] hover:bg-[#171513] hover:text-[#f6efe4]'
                )}
              >
                <Icon size={18} className={isActive ? 'text-[#67d0dc]' : 'text-[#7f7366]'} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-[#26211d] px-4 py-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2e2822] bg-[#201d1a]">
                <UserCircle2 size={19} className="text-[#d1c6b8]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#f7efe2]">
                  {role === 'super_admin' ? 'Super admin' : 'Client view'}
                </p>
                <p className="truncate text-xs text-[#8d8173]">{user?.email ?? activeCompany?.name ?? 'No company selected'}</p>
              </div>
            </div>

            <button
              onClick={() => { trackProductInteraction('view_mode_switch', { view_mode: role === 'super_admin' ? 'normal_user' : 'super_admin' }); toggleRole(); }}
              className="mt-4 w-full rounded-lg border border-[#302922] bg-[#171513] px-3 py-2 text-xs font-semibold text-[#c8b9a7] transition hover:border-[#4b4035] hover:text-[#f0e6d8]"
            >
              {role === 'super_admin' ? 'View as client' : 'Return to admin'}
            </button>

            <button
              type="button"
              onClick={() => signOutAdmin()}
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-[#d78f8f] transition hover:bg-[#2a1717]"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 md:h-screen md:overflow-y-auto">
        <header className="sticky top-0 z-20 border-b border-[var(--admin-border)] bg-[#f6f1e8]/92 backdrop-blur-xl">
          <div className="px-4 py-4 sm:px-7 lg:px-12">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--admin-muted)]">
                  {role === 'super_admin' ? 'Global operations' : 'Company operations'}
                </p>
                <h2 className="mt-1 text-2xl font-semibold text-[var(--admin-text)]">{pageTitle}</h2>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-[var(--admin-border)] bg-white/75 px-4 py-2 text-xs font-semibold text-[var(--admin-muted)]">
                  {activeCompany?.name ?? 'No company selected'}
                </div>
                <span className="h-2 w-2 rounded-full bg-[var(--admin-emerald)]" aria-label="Connected" />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 md:hidden">
              {filteredNavItems.map((item) => {
                const isActive =
                  item.path === '/admin'
                    ? location.pathname === '/admin' || location.pathname === '/admin/'
                    : location.pathname.startsWith(item.path);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-2 rounded-2xl border px-3 py-3 text-sm font-medium',
                      isActive
                        ? 'border-[var(--admin-cyan)] bg-[var(--admin-cyan-soft)] text-[var(--admin-cyan)]'
                        : 'border-[var(--admin-border)] bg-white/70 text-[var(--admin-muted)]'
                    )}
                  >
                    <Icon size={16} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </header>

        <main className="px-4 py-8 sm:px-7 lg:px-12 lg:py-12">
          <div className="mx-auto max-w-[88rem]">
            {error ? (
              <div className="mb-5">
                <AdminNotice
                  tone="error"
                  title="API connectivity issue"
                  action={
                    <button
                      type="button"
                      onClick={() => { trackProductInteraction('data_retry'); void refreshAll(); }}
                      className="rounded-xl border border-[var(--admin-rose)] px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em]"
                    >
                      Retry
                    </button>
                  }
                >
                  Calls are targeting {apiConfig.displayBaseUrl}. Check `REACT_APP_API_BASE_URL`, local proxy, CORS,
                  and backend health.
                </AdminNotice>
              </div>
            ) : null}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
